import { useReducer, useEffect, useCallback } from 'react'
import { Box, DialogContent } from '@mui/material'
import MUIDialog from '@mui/material/Dialog'
import { t } from 'i18next'
import ButtonPrimaryOutlined from 'src/components/shared/ui/ButtonPrimaryOutlined'
import {
  CheckFillIcon,
  NumberOneIcon,
  NumberTwoIcon,
  NumberThreeIcon,
  CloseRoundFullIcon
} from 'src/components/shared/ui/Icons'
import Loader from 'src/components/shared/ui/Loader/Loader'
import { LoginStagesStyles as styles } from 'src/components/shared/ui/LoginDialog/LoginStagesStyles'
import {
  LoginStageProp,
  LoginStageStatus,
  LoginStage,
  LoginStageStep
} from 'src/components/shared/ui/LoginDialog/LoginStagesTypes'
import { theme } from 'src/providers/ThemeProvider'
import DialogTitle from '../DialogTitle/DialogTitle'

export const initialStages: LoginStage[] = [
  {
    step: LoginStageStep.STEP_1,
    status: LoginStageStatus.INITIAL,
    message: 'Obteniendo Token LexNET',
    error: null,
    icon: <NumberOneIcon />
  },
  {
    step: LoginStageStep.STEP_2,
    status: LoginStageStatus.INITIAL,
    message: 'Conectando Autofirma',
    error: null,
    icon: <NumberTwoIcon />
  },
  {
    step: LoginStageStep.STEP_3,
    status: LoginStageStatus.INITIAL,
    message: 'Conectando LexNET',
    error: null,
    icon: <NumberThreeIcon />
  }
]

type Props = {
  show: boolean
  selectedStage: LoginStageProp
  close: () => void
  disabledButton: boolean
  buttonText: string
}

export default function LoginDialog({
  show,
  selectedStage,
  close,
  disabledButton,
  buttonText = t('Cancelar')
}: Props): JSX.Element {
  const reducer = (stages: LoginStage[], selectedStage: LoginStageProp) => {
    return stages.map((stage, index) => {
      stage.error = null
      if (selectedStage.step === index + 1) {
        const { status, error } = selectedStage
        stage.status = status
        if (selectedStage.status === LoginStageStatus.ERROR) {
          stage.error = error
        }
      }
      return stage
    })
  }

  const [stages, dispatchStages] = useReducer(reducer, initialStages)

  const renderStatusIcon = useCallback((status: LoginStageStatus | null): JSX.Element | null => {
    switch (status) {
      case LoginStageStatus.PROCESSING:
        return (
          <Box sx={styles.loader}>
            <Loader size="small" />
          </Box>
        )
      case LoginStageStatus.SUCCESS:
        return <CheckFillIcon htmlColor={theme.colors.seaGreen100} />
      case LoginStageStatus.ERROR:
        return <CloseRoundFullIcon htmlColor={theme.colors.persianRed100} />
      default:
        return null
    }
  }, [])

  function getClassName(status: LoginStageStatus | null): string | undefined {
    switch (status) {
      case LoginStageStatus.INITIAL:
        return 'initial'
      case LoginStageStatus.PROCESSING:
        return 'processing'
      case LoginStageStatus.SUCCESS:
        return 'success'
      case LoginStageStatus.ERROR:
        return 'error'
      default:
        return undefined
    }
  }

  function getInfoText(stage: LoginStage): string {
    switch (stage.status) {
      case LoginStageStatus.PROCESSING:
        return 'Ejecutando'
      case LoginStageStatus.SUCCESS:
        return 'Completado'
      case LoginStageStatus.ERROR:
        return 'Error'
      default:
        return ''
    }
  }

  const resetStages = () => {
    dispatchStages({
      step: LoginStageStep.STEP_1,
      status: LoginStageStatus.INITIAL,
      error: null
    })
    dispatchStages({
      step: LoginStageStep.STEP_2,
      status: LoginStageStatus.INITIAL,
      error: null
    })
    dispatchStages({
      step: LoginStageStep.STEP_3,
      status: LoginStageStatus.INITIAL,
      error: null
    })
  }

  useEffect(() => {
    if (selectedStage.step) {
      dispatchStages(selectedStage)
    }
  }, [selectedStage])

  useEffect(() => {
    if (!show) {
      resetStages()
    }
  }, [show])

  function onClose() {
    resetStages()
    close()
  }

  return (
    <MUIDialog
      open={show}
      fullWidth
      maxWidth="sm"
      fullScreen={false}
      sx={styles.container}
      className="dialog-container"
    >
      <DialogTitle id="login-dialog">{t('dialog.title.conectar_lexnet')}</DialogTitle>
      <DialogContent sx={styles.content}>
        <Box component="ul">
          {stages.map(stage => (
            <Box
              component="li"
              key={stage.message}
              className={getClassName(stage.status)}
              sx={styles.stageListItem}
            >
              <Box sx={styles.row1}>
                <Box sx={styles.statusText}>{stage.message}</Box>
                <Box sx={styles.statusMessage} className={getClassName(stage.status)}>
                  {getInfoText(stage)}
                </Box>
                <Box sx={styles.statusIcon} className={getClassName(stage.status)}>
                  {renderStatusIcon(stage.status)}
                </Box>
              </Box>
              <Box sx={styles.rowProgress}>
                <Box sx={styles.progress} />
              </Box>
              {stage.error && (
                <Box component="p" sx={styles.errorInfo}>
                  {stage.error}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <Box sx={styles.sendButton}>
        <ButtonPrimaryOutlined disabled={disabledButton} onClick={onClose}>
          {buttonText}
        </ButtonPrimaryOutlined>
      </Box>
    </MUIDialog>
  )
}
